import {
  AssetFilterBaseColumns,
  AssetFilterModel,
  GlobalAdminColumns,
  useAssetListFilter,
  useCurrentWorkspace,
  useSetAssetListFilter,
  useStatefulWorkspaceRoutines,
  useValidateAssetListOrder,
  WorkspaceType,
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Platform, ScrollView, View } from 'react-native';
import { array, boolean, string, object as yupObject } from 'yup';
import { FormContext } from '../forms';
import { DropdownFooter } from '../modals/dropdownFooter';
import { ApplyButton } from './applyButton';
import { CheckBoxSelector } from './checkBoxSelector';
import { ColumnSelection } from './columnSelection';
import { FilterType } from './filterButton';
import { FilterHeader } from './filterHeader';
import { filterStyles as styles } from './filterStyle';
import { GroupFilter } from './groupFilter';
import { SwitchToggle } from './switchToggle';

interface AssetFilterProps {
  closeFilter: () => void;
  type: FilterType;
}

export const AssetFilter = ({ closeFilter, type }: AssetFilterProps) => {
  const { t } = useTranslation();
  const workspace = useCurrentWorkspace(true);
  const workspaceRoutines = useStatefulWorkspaceRoutines(workspace?.id);
  const isWeb = Platform.OS === 'web';

  const { data: assetFilter } = useAssetListFilter();
  const validateAssetListOrder = useValidateAssetListOrder();

  const { mutateAsync: setAssetListFilter } = useSetAssetListFilter();

  const formSchema = yupObject({
    ...{ keyword: string().optional().nullable() },
    columns: array().of(string().optional().nullable()).optional().nullable(),
    groups: array().of(string().optional().nullable()).optional().nullable(),
    isCombinedGroupsFilter: boolean().optional().nullable(),
  });

  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    values: {
      columns: assetFilter?.columns,
      keyword: assetFilter?.keyword || '',
      groups: assetFilter?.groups,
      isCombinedGroupsFilter: assetFilter?.isCombinedGroupsFilter,
    },
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
    getValues,
    setValue,
    watch,
  } = formMethods;
  const selectedColumns = watch('columns');

  const isColumnsFilter = type === FilterType.ASSETCOLUMNS;
  const isGroupsFilter = type === FilterType.ASSETGROUPS;

  const resetForm = useCallback(() => {
    const isOEM = workspace?.type === WorkspaceType.Oem;
    let defaultColumns = new AssetFilterModel().columns;
    if (defaultColumns) {
      if (isOEM) {
        // Add productName after description for OEM
        const descriptionIndex = defaultColumns.indexOf('description');
        if (
          descriptionIndex !== -1 &&
          !defaultColumns.includes('productName')
        ) {
          defaultColumns = [
            ...defaultColumns.slice(0, descriptionIndex + 1),
            'productName',
            ...defaultColumns.slice(descriptionIndex + 1),
          ];
        }
      } else {
        // Filter out productName if not OEM
        defaultColumns = defaultColumns.filter((col) => col !== 'productName');
      }
    }
    reset({
      ...selectedColumns,
      ...(isColumnsFilter && { columns: defaultColumns }),
      ...(isGroupsFilter && { groups: [], isCombinedGroupsFilter: false }),
    });
  }, [reset, isColumnsFilter, isGroupsFilter, selectedColumns, workspace]);

  const onReset = async () => {
    const isOEM = workspace?.type === WorkspaceType.Oem;
    let defaultColumns = new AssetFilterModel().columns;
    if (defaultColumns) {
      if (isOEM) {
        // Add productName after description for OEM
        const descriptionIndex = defaultColumns.indexOf('description');
        if (
          descriptionIndex !== -1 &&
          !defaultColumns.includes('productName')
        ) {
          defaultColumns = [
            ...defaultColumns.slice(0, descriptionIndex + 1),
            'productName',
            ...defaultColumns.slice(descriptionIndex + 1),
          ];
        }
      } else {
        // Filter out productName if not OEM
        defaultColumns = defaultColumns.filter((col) => col !== 'productName');
      }
    }
    const updatedFields = {
      ...(isColumnsFilter && { columns: defaultColumns }),
      ...(isGroupsFilter && { groups: [], isCombinedGroupsFilter: false }),
    };

    resetForm();

    const mergedFilters = { ...assetFilter, ...updatedFields };
    // Ensure productName is handled correctly based on OEM status
    if (mergedFilters.columns) {
      if (isOEM) {
        // Add productName after description if not present
        const descriptionIndex = mergedFilters.columns.indexOf('description');
        if (
          descriptionIndex !== -1 &&
          !mergedFilters.columns.includes('productName')
        ) {
          mergedFilters.columns = [
            ...mergedFilters.columns.slice(0, descriptionIndex + 1),
            'productName',
            ...mergedFilters.columns.slice(descriptionIndex + 1),
          ];
        }
      } else {
        // Filter out productName if not OEM
        mergedFilters.columns = mergedFilters.columns.filter(
          (col: string) => col !== 'productName'
        );
      }
    }
    await applyFilter(new AssetFilterModel(mergedFilters));
  };

  const onSubmit = async (data: any) => {
    if (!data?.keyword.length) {
      delete data.keyword;
    }
    const isOEM = workspace?.type === WorkspaceType.Oem;
    // Filter out productName if not OEM
    if (!isOEM && data.columns) {
      data.columns = data.columns.filter(
        (col: string) => col !== 'productName'
      );
    }
    await applyFilter(new AssetFilterModel(data));
  };

  const applyFilter = async (filterModel: any) => {
    await Promise.all([
      setAssetListFilter(filterModel),
      validateAssetListOrder(filterModel),
    ]);

    closeFilter();
  };

  const canReset = () => {
    return !Object.values(getValues()).every((value) => value === '');
  };

  const onColumnChange = (column: string) => {
    if (selectedColumns?.includes(column)) {
      setValue(
        'columns',
        selectedColumns.filter((c) => c !== column),
        { shouldDirty: true, shouldTouch: true, shouldValidate: true }
      );
    } else {
      setValue('columns', [...(selectedColumns || []), column], {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    }
  };

  useEffect(() => {
    if (!assetFilter) {
      resetForm();
    } else {
      // Filter out productName from loaded filter if not OEM
      const isOEM = workspace?.type === WorkspaceType.Oem;
      if (!isOEM && assetFilter.columns?.includes('productName')) {
        const updatedColumns = assetFilter.columns.filter(
          (col: string) => col !== 'productName'
        );
        setValue('columns', updatedColumns, { shouldDirty: false });
      }
    }
  }, [assetFilter, resetForm, workspace, setValue]);

  const renderGroups = () => {
    return (
      workspace && (
        <View style={styles.groupsSection}>
          <GroupFilter
            fieldName="groups"
            workspaceId={workspace?.id}
            headerStyle={styles.filterRowHeader}
          />
        </View>
      )
    );
  };

  const renderStandardData = () => {
    const isOEM = workspace?.type === WorkspaceType.Oem;
    const baseColumns = isOEM
      ? AssetFilterBaseColumns
      : AssetFilterBaseColumns.filter(
          (col) => col !== 'productName' && col !== 'addons'
        );

    return (
      <ColumnSelection
        baseColumns={baseColumns}
        adminColumns={GlobalAdminColumns}
        i18nPrefix="assets.filter"
      >
        {workspaceRoutines?.map((routine) => (
          <CheckBoxSelector
            key={routine.id}
            option={`routine:${routine.id}`}
            options={selectedColumns}
            onChange={onColumnChange}
            label={routine.name!}
          />
        ))}
      </ColumnSelection>
    );
  };

  return (
    <FormContext.Provider value={formMethods}>
      {!isWeb && (
        <FilterHeader
          canReset={canReset}
          onReset={onReset}
          goBack={closeFilter}
        />
      )}
      <View style={styles.container}>
        {isGroupsFilter && (
          <SwitchToggle
            fieldName="isCombinedGroupsFilter"
            label={t('assets.filter.filterToggle')}
          />
        )}
        <View
          style={[
            styles.scrollViewContainer,
            isWeb ? { height: 416 } : { marginBottom: 30 },
          ]}
        >
          <ScrollView
            contentContainerStyle={styles.scrollViewContent}
            scrollEnabled={true}
            showsVerticalScrollIndicator={true}
          >
            {isGroupsFilter && renderGroups()}
            {isColumnsFilter && renderStandardData()}
          </ScrollView>
        </View>

        {isWeb ? (
          <DropdownFooter
            isDirty={isDirty}
            onSubmit={handleSubmit(onSubmit)}
            canReset={canReset}
            onReset={onReset}
          />
        ) : (
          <ApplyButton isDirty={isDirty} onSubmit={handleSubmit(onSubmit)} />
        )}
      </View>
    </FormContext.Provider>
  );
};
